import {
    FeatureUserGroup,
    ILocationPermissionsResponse,
    ILocationPermissionsUpdateRequest,
    LocationPermissionsUpdateRequest,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { MultiSelectOption } from 'components/forms/multiselect/MultiSelect';
import { MultiSelectInlineEditor } from 'components/inline_edit/MultiSelectInlineEditor';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';

const OPTIONS: MultiSelectOption<FeatureUserGroup>[] = [
    { title: 'Global Admin', value: FeatureUserGroup.GlobalAdmin },
    { title: 'Sentry', value: FeatureUserGroup.Sentry },
    { title: 'Sales', value: FeatureUserGroup.Sales },
    { title: 'Admin', value: FeatureUserGroup.Admin },
    { title: 'User', value: FeatureUserGroup.User },
];

type Perms = ILocationPermissionsUpdateRequest;
type UserGroupPerms = {
    // exclude non-bools (invalid for this component)
    [K in keyof Perms as Perms[K] extends FeatureUserGroup[] ? K : never]?: Perms[K];
};
type PermsKey = keyof UserGroupPerms;

export type LocationUserGroupCellProps = {
    locationId: string;
    data: ILocationPermissionsResponse;
    permsKey: PermsKey;
    onChange: () => void;
};

export function LocationUserGroupCell({
    locationId,
    data,
    permsKey,
    onChange,
}: LocationUserGroupCellProps) {
    const { canEditLocationFeaturePermissions } = usePermissions();
    const { showToast } = useNotificationContext();
    const { mutate: updatePermission, isPending } = useMutation({
        meta: { name: 'update-location-permissions' },
        mutationFn: async (value: FeatureUserGroup[]) => {
            await sdClient.updateLocationPermissions({
                id: locationId,
                body: new LocationPermissionsUpdateRequest({
                    ...data,
                    [permsKey]: value,
                }),
            });
        },

        onSuccess: () => {
            onChange();
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to save location value unit',
            }),
    });

    const selectedItems = new Set(data[permsKey]);

    return (
        <MultiSelectInlineEditor
            value={OPTIONS.filter((item) => selectedItems.has(item.value))}
            multiSelectConfig={{
                options: OPTIONS,
            }}
            onConfirm={(v) => {
                if (v !== undefined) {
                    updatePermission(v.map((item) => item.value as FeatureUserGroup));
                }
            }}
            isDisabled={!canEditLocationFeaturePermissions || isPending}
        />
    );
}
